import type { PropertyValue } from '@skop-app/lib-domain/types'

import type { SupportedKeys } from '@/services/property'

type UnknownRecord = Record<string, unknown>

export type PropertyEntry = {
  key: string
  values?: unknown
  type?: PropertyValue | string
}

export type SupportedKeyLike = Pick<SupportedKeys, 'key' | 'type'>
export type RefDataByKey = Partial<Record<string, Record<string, string>>>
export type CellEditorPropertyMeta = {
  type?: PropertyValue | string
  supportedType?: PropertyValue | string
  isRef?: boolean
  view?: string
  isMulti?: boolean
  isLocked?: boolean
}

const REMOVED_PROPERTY_ENTRY_KEYS = '__removedPropertyEntryKeys'
const SETTER_SIDE_EFFECT_FIELD_KEYS = '__setterSideEffectFieldKeys'
const LOCALIZED_COLUMN_KEY_PATTERN = /^i18n__(.+)__([A-Z]{2})$/
const LEGACY_LOCALIZED_TOP_LEVEL_BASE_KEYS = new Set([
  'description',
  'complement',
])
const LEGACY_LOCALIZED_TOP_LEVEL_LANGUAGES = new Set(['EN', 'NL'])

type RowWithRemovedPropertyEntryKeys = {
  [REMOVED_PROPERTY_ENTRY_KEYS]?: Set<string>
}

type RowWithSetterSideEffectFieldKeys = {
  [SETTER_SIDE_EFFECT_FIELD_KEYS]?: Set<string>
}

export const NESTED_ROOTS = new Set(['dimension', 'quantity', 'price'])
const TOP_LEVEL_ONLY_LIST_KEYS = new Set(['action'])
const PROPERTIES_ONLY_TYPES = new Set(['FORMULA'])

export const NESTED_NUMERIC_KEYS = new Set([
  'price.amount',
  'price.discountAmount',
  'price.discountRatio',
  'quantity.number',
  'dimension.width',
  'dimension.height',
  'dimension.length',
  'dimension.diameter',
  'dimension.volume',
])

export const isCustomField = (key: string): boolean => key.includes('_')

export const resolveCanonicalFieldStorageKey = (key: string): string => {
  const match = LOCALIZED_COLUMN_KEY_PATTERN.exec(key)
  if (!match) return key

  const [, propertyKey, language] = match
  if (
    propertyKey &&
    language &&
    LEGACY_LOCALIZED_TOP_LEVEL_BASE_KEYS.has(propertyKey) &&
    LEGACY_LOCALIZED_TOP_LEVEL_LANGUAGES.has(language)
  ) {
    return `${propertyKey}${language}`
  }

  return key
}

export const mustPersistOnlyInProperties = (params: {
  key: string
  type?: PropertyValue | string
}): boolean => {
  const { key, type } = params
  const storageKey = resolveCanonicalFieldStorageKey(key)
  return (
    isCustomField(storageKey) || PROPERTIES_ONLY_TYPES.has(String(type ?? ''))
  )
}

export const isReferencesView = (view?: string): boolean =>
  view === 'references'

export const markRemovedPropertyEntry = (
  row: Record<string, unknown>,
  key: string,
) => {
  const target = row as RowWithRemovedPropertyEntryKeys
  if (!(target[REMOVED_PROPERTY_ENTRY_KEYS] instanceof Set)) {
    Object.defineProperty(row, REMOVED_PROPERTY_ENTRY_KEYS, {
      value: new Set<string>(),
      enumerable: false,
      configurable: true,
      writable: true,
    })
  }

  target[REMOVED_PROPERTY_ENTRY_KEYS]?.add(key)
}

export const copyRemovedPropertyEntryMarkers = <
  TRow extends Record<string, unknown>,
>(
  source: Record<string, unknown>,
  target: TRow,
): TRow => {
  if (source === target) return target

  const sourceKeys = (source as RowWithRemovedPropertyEntryKeys)[
    REMOVED_PROPERTY_ENTRY_KEYS
  ]
  if (!(sourceKeys instanceof Set) || sourceKeys.size === 0) return target

  const targetRecord = target as RowWithRemovedPropertyEntryKeys
  const targetKeys = targetRecord[REMOVED_PROPERTY_ENTRY_KEYS]
  if (targetKeys instanceof Set) {
    sourceKeys.forEach((key) => targetKeys.add(key))
    return target
  }

  Object.defineProperty(target, REMOVED_PROPERTY_ENTRY_KEYS, {
    value: new Set(sourceKeys),
    enumerable: false,
    configurable: true,
    writable: true,
  })

  return target
}

export const hasRemovedPropertyEntry = (
  row: Record<string, unknown>,
  key: string,
): boolean => {
  const target = row as RowWithRemovedPropertyEntryKeys
  return target[REMOVED_PROPERTY_ENTRY_KEYS]?.has(key) === true
}

export const markSetterSideEffectFields = (
  row: Record<string, unknown>,
  fields: Iterable<string | undefined>,
) => {
  const normalizedFields = Array.from(fields).filter(
    (field): field is string => typeof field === 'string' && field.length > 0,
  )
  if (normalizedFields.length === 0) return

  const target = row as RowWithSetterSideEffectFieldKeys
  if (!(target[SETTER_SIDE_EFFECT_FIELD_KEYS] instanceof Set)) {
    Object.defineProperty(row, SETTER_SIDE_EFFECT_FIELD_KEYS, {
      value: new Set<string>(),
      enumerable: false,
      configurable: true,
      writable: true,
    })
  }

  normalizedFields.forEach((field) =>
    target[SETTER_SIDE_EFFECT_FIELD_KEYS]?.add(field),
  )
}

export const consumeSetterSideEffectFields = (
  row: Record<string, unknown> | undefined,
): string[] => {
  const target = row as RowWithSetterSideEffectFieldKeys | undefined
  const fields = target?.[SETTER_SIDE_EFFECT_FIELD_KEYS]
  if (!(fields instanceof Set)) return []

  const values = Array.from(fields)
  fields.clear()
  return values
}

export const getCellEditorProperty = (
  colDef: { cellEditorParams?: unknown } | undefined,
  fallbackType?: PropertyValue | string,
): CellEditorPropertyMeta => {
  const rawParams = colDef?.cellEditorParams
  if (!rawParams || typeof rawParams !== 'object') {
    return { type: fallbackType }
  }

  const property = (
    rawParams as { property?: CellEditorPropertyMeta | undefined }
  ).property
  return {
    ...property,
    type: property?.type ?? fallbackType,
  }
}

const toFirstArrayValue = (value: unknown): unknown => {
  if (!Array.isArray(value)) return value
  return value.length > 0 ? value[0] : undefined
}

const readObjectValue = (
  value: unknown,
  key: 'value' | 'id' | 'label',
): unknown => {
  if (!value || typeof value !== 'object') return undefined
  const record = value as UnknownRecord
  return record[key]
}

const normalizePrimitive = (
  value: unknown,
): { kind: 'empty' } | { kind: 'value'; value: string | number | boolean } => {
  if (value === null || value === undefined) return { kind: 'empty' }
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed.length > 0
      ? { kind: 'value', value: trimmed }
      : { kind: 'empty' }
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? { kind: 'value', value } : { kind: 'empty' }
  }
  if (typeof value === 'boolean') return { kind: 'value', value }
  return { kind: 'empty' }
}

export const parseEditorScalar = (
  newValue: unknown,
): { kind: 'empty' } | { kind: 'value'; value: string | number | boolean } => {
  const scalar = toFirstArrayValue(newValue)
  if (scalar && typeof scalar === 'object') {
    const optionValue =
      readObjectValue(scalar, 'value') ??
      readObjectValue(scalar, 'id') ??
      readObjectValue(scalar, 'label')
    return normalizePrimitive(optionValue)
  }
  return normalizePrimitive(scalar)
}

export const extractOptionValue = (value: unknown): unknown => {
  if (value && typeof value === 'object' && 'value' in value) {
    return (value as UnknownRecord).value
  }
  return value
}

export const extractIdLike = (newValue: unknown): string | undefined => {
  const scalar = toFirstArrayValue(newValue)
  if (scalar === null || scalar === undefined) return undefined
  if (typeof scalar === 'string') {
    const trimmed = scalar.trim()
    return trimmed.length > 0 ? trimmed : undefined
  }
  if (typeof scalar === 'number' || typeof scalar === 'boolean') {
    return String(scalar)
  }
  if (typeof scalar === 'object') {
    const idValue = readObjectValue(scalar, 'id')
    if (typeof idValue === 'string' && idValue.trim().length > 0) {
      return idValue
    }
    const value = readObjectValue(scalar, 'value')
    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      const asString = String(value).trim()
      return asString.length > 0 ? asString : undefined
    }
  }
  return undefined
}

export const isNestedKey = (key: string): boolean => {
  if (!key.includes('.')) return false
  const [root] = key.split('.')
  return Boolean(root) && NESTED_ROOTS.has(root)
}

export const isNestedNumericKey = (key: string): boolean =>
  NESTED_NUMERIC_KEYS.has(key)

export const splitNestedKey = (
  key: string,
): { rootKey: string; subKey: string } | null => {
  if (!isNestedKey(key)) return null
  const [rootKey, subKey] = key.split('.')
  if (!rootKey || !subKey) return null
  return { rootKey, subKey }
}

const isEmptyNumericInput = (value: unknown): boolean =>
  value === null ||
  value === undefined ||
  (typeof value === 'string' && value.trim().length === 0)

const toNumberOrUndefined = (value: unknown): number | undefined => {
  if (typeof value === 'number')
    return Number.isFinite(value) ? value : undefined
  const normalized = String(value).replace(',', '.').trim()
  if (normalized.length === 0) return undefined
  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : undefined
}

const toScalarOrNull = (value: unknown): string | number | boolean | null => {
  const parsed = parseEditorScalar(value)
  return parsed.kind === 'empty' ? null : parsed.value
}

export const setNestedTopLevelValue = (
  rowClone: Record<string, unknown>,
  key: string,
  newValue: unknown,
  options: { parseScalars?: boolean; defaultPriceCurrency?: string } = {},
): boolean => {
  const nested = splitNestedKey(key)
  if (!nested) return false

  const { rootKey, subKey } = nested
  const existingRoot = rowClone[rootKey]
  const nextRoot: Record<string, unknown> =
    existingRoot &&
    typeof existingRoot === 'object' &&
    !Array.isArray(existingRoot)
      ? { ...(existingRoot as Record<string, unknown>) }
      : {}

  const parseScalars = options.parseScalars !== false
  if (isNestedNumericKey(key)) {
    const numericInput = parseScalars ? toScalarOrNull(newValue) : newValue
    const parsedNumber = toNumberOrUndefined(numericInput)
    if (parsedNumber === undefined) {
      if (key !== 'quantity.number' || !isEmptyNumericInput(numericInput))
        return false
      nextRoot[subKey] = null
      rowClone[rootKey] = nextRoot
      return true
    }
    const hadPriceAmount = nextRoot.amount != null
    nextRoot[subKey] = parsedNumber

    if (key === 'price.amount') {
      const currency = nextRoot.currency
      if (typeof currency !== 'string' || currency.length === 0) {
        nextRoot.currency =
          !hadPriceAmount && (currency == null || currency === '')
            ? (options.defaultPriceCurrency ?? 'EUR')
            : 'EUR'
        markSetterSideEffectFields(rowClone, ['price.currency'])
      }
    }
    if (key === 'quantity.number') {
      const unit = nextRoot.unit
      if (typeof unit !== 'string' || unit.length === 0) {
        nextRoot.unit = 'u'
        markSetterSideEffectFields(rowClone, ['quantity.unit'])
      }
    }
  } else {
    nextRoot[subKey] = parseScalars
      ? toScalarOrNull(newValue)
      : (newValue ?? null)
  }

  rowClone[rootKey] = nextRoot
  return true
}

/**
 * Persistence contract shared by inventory and import:
 * - custom fields (detected via isCustomField()) always live in properties[]
 * - non-custom LIST fields live in properties[], except top-level-only keys like action
 * - all other non-custom supported scalar types stay top-level
 *
 * If this contract changes, update createPropertiesCleaner/buildSupportedFieldSnapshot
 * and their tests together.
 */
export const needsProperties = (params: {
  key: string
  type?: PropertyValue | string
}): boolean => {
  const { key, type } = params
  if (mustPersistOnlyInProperties({ key, type })) return true
  if (TOP_LEVEL_ONLY_LIST_KEYS.has(key)) return false
  return type === 'LIST'
}

export const buildSupportedByKey = (
  supportedKeys: SupportedKeyLike[] | undefined,
): Record<string, SupportedKeyLike> => {
  if (!Array.isArray(supportedKeys) || supportedKeys.length === 0) return {}
  return supportedKeys.reduce<Record<string, SupportedKeyLike>>((acc, item) => {
    if (!item?.key) return acc
    acc[item.key] = item
    return acc
  }, {})
}

const isPropertyEntryLike = (value: unknown): value is PropertyEntry =>
  Boolean(value && typeof value === 'object' && 'key' in value)

const isPropertyPersistedInProperties = (
  property: PropertyEntry,
  supportedByKey: Record<string, SupportedKeyLike>,
): boolean => {
  if (!property.key) return false

  const supportedType = supportedByKey[property.key]?.type
  const effectiveType = supportedType ?? property.type
  if (
    mustPersistOnlyInProperties({
      key: property.key,
      type: effectiveType,
    })
  ) {
    return true
  }
  if (TOP_LEVEL_ONLY_LIST_KEYS.has(property.key)) return false
  return effectiveType === 'LIST'
}

export const cleanPropertiesForSave = (
  properties: PropertyEntry[] | undefined,
  supportedKeys: SupportedKeyLike[] | undefined,
): PropertyEntry[] => {
  const clean = createPropertiesCleaner(supportedKeys)
  return clean(properties)
}

const shouldHydrateTopLevelProperty = (
  property: PropertyEntry,
  supportedByKey: Record<string, SupportedKeyLike>,
): boolean => {
  if (typeof property.key !== 'string' || !property.key) return false
  if (property.key.includes('.')) return false
  if (isPropertyPersistedInProperties(property, supportedByKey)) return false

  const effectiveType = supportedByKey[property.key]?.type ?? property.type
  return effectiveType !== 'LIST'
}

export const hydrateTopLevelPropertiesFromEntries = <
  Row extends Record<string, unknown>,
>(
  row: Row,
  supportedKeys: SupportedKeyLike[] | undefined,
): Row => {
  if (!Array.isArray(row.properties) || row.properties.length === 0) return row

  const supportedByKey = buildSupportedByKey(supportedKeys)
  let nextRow: Row | undefined

  row.properties.forEach((property) => {
    if (!isPropertyEntryLike(property)) return
    if (!shouldHydrateTopLevelProperty(property, supportedByKey)) return
    const currentRow = nextRow ?? row
    if (
      currentRow[property.key] !== undefined &&
      currentRow[property.key] !== null
    ) {
      return
    }
    if (!Array.isArray(property.values) || property.values.length === 0) return

    nextRow ??= { ...row }
    ;(nextRow as Record<string, unknown>)[property.key] = property.values[0]
  })

  return nextRow ?? row
}

export const createPropertiesCleaner = (
  supportedKeys: SupportedKeyLike[] | undefined,
): ((properties: PropertyEntry[] | undefined) => PropertyEntry[]) => {
  const supportedByKey = buildSupportedByKey(supportedKeys)

  return (properties: PropertyEntry[] | undefined): PropertyEntry[] => {
    if (!Array.isArray(properties) || properties.length === 0) return []
    return properties.filter((property) =>
      isPropertyPersistedInProperties(property, supportedByKey),
    )
  }
}

export const matchValues = (
  value: string[] | string | null | undefined,
  refDataByKey: Record<string, string> | undefined,
): { id: string; label: string }[] => {
  const values = Array.isArray(value) ? value : value ? [value] : []
  return values.map((item) => {
    return { id: item, label: refDataByKey?.[item] ?? item }
  })
}

export const matchValue = (
  value: string | null | undefined,
  refDataByKey: Record<string, string> | undefined,
): { value: string; label: string } => {
  if (!value) return { value: value ?? '', label: value ?? 'Vide' }
  return { label: refDataByKey?.[value] ?? value, value }
}

export const findOption = (
  value: string,
  options: readonly { label: string; value: string }[],
): { label: string; value: string } => {
  const found = options.find((option) => option.value === value)
  if (found) {
    return { label: found.label, value: found.value }
  }

  return { label: value, value }
}
